import {
  KoButton,
  KoForm,
  KoModal,
  KoNano,
  KoTable,
  KoText,
  KoTitle,
  KoToast,
  KoTooltips,
  KoTag,
  KoSelect,
  KoInput,
  KoTextArea,
  KoFormBuilder
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import {
  COLUMNS_QA,
  SEVERITY_OPTIONS,
  QA_TYPE_OPTIONS,
  QA_RESULT_CONFIG
} from '../constants';
import QACrud from './crud/QACrud';
import ReleaseSubNav from './ReleaseSubNav';
import styles from './releases.module.scss';

const MOCK_TESTS = [
  { testId: 'QA-001', ticketId: 'KWP-882', title: 'Dashboard widgets render correctly on all viewports', type: 'regression', tester: 'QA Bot', environment: 'qa', result: 'passed' },
  { testId: 'QA-002', ticketId: 'KWP-882', title: 'Revenue chart data accuracy vs raw metrics API', type: 'api_contract', tester: 'Sarah Kim', environment: 'qa', result: 'passed' },
  { testId: 'QA-003', ticketId: 'KWP-891', title: 'Token refresh handles 50 concurrent requests', type: 'performance', tester: 'QA Bot', environment: 'qa', result: 'passed' },
  { testId: 'QA-004', ticketId: 'KWP-891', title: 'Auth flow works after token expiry during idle session', type: 'regression', tester: 'Alice Park', environment: 'qa', result: 'in_progress' },
  { testId: 'QA-005', ticketId: 'KWP-905', title: 'User service migration backward compatibility', type: 'backward_compat', tester: 'Bob Chen', environment: 'qa', result: 'failed' },
  { testId: 'QA-006', ticketId: 'KWP-905', title: 'Database migration dry run on staging', type: 'db_migration', tester: 'QA Bot', environment: 'qa', result: 'passed' },
  { testId: 'QA-007', ticketId: 'KWP-912', title: 'Notification preferences persist across sessions', type: 'regression', tester: 'Sarah Kim', environment: 'qa', result: 'not_started' },
  { testId: 'QA-008', ticketId: 'KWP-912', title: 'Push notification delivery within 5s SLA', type: 'performance', tester: 'QA Bot', environment: 'qa', result: 'not_started' },
  { testId: 'QA-009', ticketId: 'KWP-870', title: 'Dependency update smoke test - all pages load', type: 'smoke', tester: 'QA Bot', environment: 'qa', result: 'passed' },
  { testId: 'QA-010', ticketId: 'KWP-870', title: 'Security scan after dependency updates', type: 'security', tester: 'QA Bot', environment: 'qa', result: 'passed' }
];

const MOCK_BUGS = [
  { bugId: 'BUG-001', ticketId: 'KWP-905', title: 'User avatar not loading after migration', severity: 'sev3', status: 'open', reportedBy: 'Bob Chen' },
  { bugId: 'BUG-002', ticketId: 'KWP-882', title: 'Chart tooltip flickers on mobile Safari', severity: 'sev4', status: 'open', reportedBy: 'Sarah Kim' }
];

function QATesting() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [tests, setTests] = useState([]);
  const [bugs, setBugs] = useState([]);
  const [isOpenTest, setOpenTest] = useState(false);
  const [isOpenBug, setOpenBug] = useState(false);
  const [currentRecord, setCurrentRecord] = useState();
  const [testForm] = KoForm.useForm();
  const [bugForm] = KoForm.useForm();

  const releaseBasePath = workspaceId
    ? `/workspace/${workspaceId}/development/release`
    : '/release';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Releases', path: releaseBasePath },
      { title: 'QA Testing' }
    ]));
    setTests(MOCK_TESTS);
    setBugs(MOCK_BUGS);
  }, [workspaceId]);

  const handleCreateTest = (values) => {
    values['testId'] = KoNano('QA');
    values['result'] = 'not_started';
    setTests((prev) => [...prev, values]);
    setOpenTest(false);
    testForm.resetFields();
    KoToast.success('Test case added!');
  };

  const handleFileBug = (values) => {
    values['bugId'] = KoNano('BUG');
    values['status'] = 'open';
    values['reportedBy'] = 'You';
    setBugs((prev) => [...prev, values]);
    setOpenBug(false);
    bugForm.resetFields();
    KoToast.success('Bug filed and assigned!');
  };

  const updateResult = (testId, result) => {
    setTests((prev) => prev.map((t) => t.testId === testId ? { ...t, result } : t));
    KoToast.success(`Test marked as ${result}`);
  };

  const passed = tests.filter((t) => t.result === 'passed').length;
  const failed = tests.filter((t) => t.result === 'failed').length;
  const inProgress = tests.filter((t) => t.result === 'in_progress').length;
  const notStarted = tests.filter((t) => !t.result || t.result === 'not_started').length;

  const typeGroups = {};
  tests.forEach(t => {
    const type = t.type || 'other';
    if (!typeGroups[type]) typeGroups[type] = { total: 0, passed: 0 };
    typeGroups[type].total++;
    if (t.result === 'passed') typeGroups[type].passed++;
  });

  const enrichedCols = COLUMNS_QA.map((col) => {
    if (col.id === 'result' || col.key === 'result') {
      return {
        ...col,
        render: (val, record) => {
          const cfg = QA_RESULT_CONFIG[val] || QA_RESULT_CONFIG.not_started;
          if (val === 'passed' || val === 'failed') {
            return <KoTag color={cfg.color}>{cfg.label}</KoTag>;
          }
          return (
            <div className={styles.resultActions}>
              <KoButton className='btn' label='Pass' type='text' style={{ color: 'var(--palette-success-main)' }} onClick={() => updateResult(record.testId, 'passed')} />
              <KoButton className='btn' label='Fail' type='text' danger onClick={() => updateResult(record.testId, 'failed')} />
            </div>
          );
        }
      };
    }
    if (col.id === 'ticketId' || col.key === 'ticketId') {
      return { ...col, render: (val) => val ? <span className={styles.ticketChip}>{val}</span> : '-' };
    }
    if (col.id === 'type' || col.key === 'type') {
      return {
        ...col,
        render: (val) => {
          const opt = QA_TYPE_OPTIONS.find(o => o.value === val);
          return val ? <KoTag>{opt?.label || val}</KoTag> : '-';
        }
      };
    }
    return col;
  });

  const columnsRight = [{
    title: '', dataIndex: 'x', key: 'x', fixed: 'right', width: 60,
    render: (_, record) => (
      <div className='tableBtn columnsRight'>
        <KoTooltips title='Edit'>
          <KoButton className='btn' iconBtn icon={<KoIcon width={14} height={14} name='EditOutlined' />}
            onClick={() => { setCurrentRecord(record); setOpenTest(true); }} />
        </KoTooltips>
      </div>
    )
  }];

  return (
    <div className='tableCard'>
      <ReleaseSubNav activeKey='qa' />

      <KoToolBar
        title='QA Validation'
        search={{ visible: true, onChange: () => {}, placeholder: 'Search test cases' }}
        filter={{ visible: false, options: [] }}
        buttonList={[
          { label: 'File Bug', icon: <KoIcon color='#565555' name='BugOutlined' width={16} height={16} />, onClick: () => setOpenBug(true) },
          { label: 'Add Test Case', type: 'primary', icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />, onClick: () => { setCurrentRecord(); setOpenTest(true); } }
        ]}
      />

      <div className={styles.flowBanner}>
        <KoIcon name='CheckCircleOutlined' width={14} height={14} />
        <KoText text='Step 2: QA Validation — All tests must pass before advancing to UAT Sign-off' />
      </div>

      <div className={styles.statsRow}>
        {[
          { num: passed, label: 'Passed', color: 'var(--palette-success-main)' },
          { num: failed, label: 'Failed', color: 'var(--palette-error-main)' },
          { num: inProgress, label: 'In Progress', color: 'var(--palette-warning-main)' },
          { num: notStarted, label: 'Not Started', color: 'var(--palette-text-secondary)' }
        ].map((s) => (
          <div key={s.label} className={styles.qaStatCard}>
            <span className={styles.qaStatNum} style={{ color: s.color }}>{s.num}</span>
            <span className={styles.qaStatLabel}>{s.label}</span>
          </div>
        ))}
      </div>

      {Object.keys(typeGroups).length > 0 && (
        <div className={styles.typeProgressSection}>
          <KoText className={styles.sectionLabel} text='VALIDATION BY TYPE' />
          <div className={styles.typeProgressGrid}>
            {Object.entries(typeGroups).map(([type, data]) => {
              const pct = data.total > 0 ? Math.round((data.passed / data.total) * 100) : 0;
              const opt = QA_TYPE_OPTIONS.find(o => o.value === type);
              return (
                <div key={type} className={styles.typeProgressItem}>
                  <div className={styles.typeProgressHeader}>
                    <KoText text={opt?.label || type} />
                    <KoText className={styles.typeProgressPct} text={`${data.passed}/${data.total}`} />
                  </div>
                  <div className={styles.typeProgressBar}>
                    <div className={styles.typeProgressFill} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <KoTable rowKey='testId' data={{ columns: enrichedCols, rows: tests }} extraColumnsRight={columnsRight} maxHeight={100} />

      {bugs.length > 0 && (
        <div className={styles.bugsSection}>
          <KoTitle level={5} text='Bug Reports' />
          <div className={styles.bugList}>
            {bugs.map((bug) => (
              <div key={bug.bugId} className={styles.bugRow}>
                <KoTag color={bug.status === 'open' ? 'error' : 'success'}>{bug.status === 'open' ? 'Open' : 'Fixed'}</KoTag>
                <div className={styles.bugInfo}>
                  <KoText className={styles.bugTitle} text={bug.title} />
                  <KoText className={styles.bugMeta} text={`${bug.ticketId} · ${bug.severity} · Found by ${bug.reportedBy}`} />
                </div>
                <KoTag color={bug.severity === 'sev1' ? 'error' : bug.severity === 'sev2' ? 'orange' : 'default'}>{bug.severity}</KoTag>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`${styles.qaGateStatus} ${passed === tests.length && tests.length > 0 ? styles.qaGateReady : ''}`}>
        {passed === tests.length && tests.length > 0 ? (
          <>
            <KoIcon name='CheckCircleOutlined' width={24} height={24} color='var(--palette-success-main)' />
            <KoText text='All QA tests passed — Ready to advance to UAT' />
          </>
        ) : (
          <>
            <KoIcon name='ClockCircleOutlined' width={24} height={24} color='var(--palette-warning-main)' />
            <KoText text={`QA in progress — ${passed} of ${tests.length} tests passed. Cannot advance to UAT until all pass.`} />
          </>
        )}
      </div>

      <KoModal title={currentRecord?.testId ? 'Update Test Case' : 'Add Test Case'} centered open={isOpenTest}
        afterClose={() => setCurrentRecord()} handleOk={() => setOpenTest(false)} onClose={() => setOpenTest(false)}
        width={'min(760px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => setOpenTest(false)} />,
          <KoButton key='submit' type='primary' label={currentRecord?.testId ? 'Update' : 'Add'} onClick={() => testForm.submit()} />
        ]}
      >
        <QACrud form={testForm} initialValues={currentRecord} handleFinish={handleCreateTest} testerOptions={[
          { label: 'Alice Park', value: 'alice' }, { label: 'Bob Chen', value: 'bob' },
          { label: 'Sarah Kim', value: 'sarah' }, { label: 'QA Bot', value: 'qa_bot' }
        ]} />
      </KoModal>

      <KoModal title='File QA Bug Report' centered open={isOpenBug}
        handleOk={() => setOpenBug(false)} onClose={() => setOpenBug(false)}
        width={'min(680px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => setOpenBug(false)} />,
          <KoButton key='submit' type='primary' label='File Bug' onClick={() => bugForm.submit()} />
        ]}
      >
        <BugCrudInline form={bugForm} handleFinish={handleFileBug} />
      </KoModal>
    </div>
  );
}

function BugCrudInline({ form, handleFinish }) {
  const SeverityWidget = ({ value, onChange }) => (
    <KoSelect value={value} onChange={onChange} placeholder='Severity' options={SEVERITY_OPTIONS} />
  );
  const EnvWidget = ({ value, onChange }) => (
    <KoSelect value={value} onChange={onChange} placeholder='Environment'
      options={[{ label: 'QA', value: 'qa' }, { label: 'UAT', value: 'uat' }, { label: 'Production', value: 'production' }]} />
  );

  const meta = {
    columns: 4, formItemLayout: null, colon: true,
    fields: [
      { key: 'ticketId', label: 'Linked Ticket', colSpan: 2, widget: KoInput, widgetProps: { placeholder: 'KWP-...', style: { fontFamily: 'monospace' } } },
      { key: 'severity', label: 'Severity', colSpan: 1, widget: SeverityWidget, forwardRef: true },
      { key: 'environment', label: 'Environment', colSpan: 1, widget: EnvWidget, forwardRef: true },
      { key: 'title', label: 'Bug Title', colSpan: 4, widget: KoInput, widgetProps: { placeholder: 'Describe the bug clearly' } },
      { key: 'steps', label: 'Steps to Reproduce', colSpan: 4, widget: KoTextArea, widgetProps: { placeholder: '1. Go to...\n2. Click...\n3. Observe...', rows: 4 } }
    ]
  };

  return (
    <KoForm form={form} layout='vertical' onFinish={handleFinish}>
      <KoFormBuilder form={form} meta={meta} />
    </KoForm>
  );
}

export default QATesting;
